import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import HeroGalleryLightbox from "./HeroGalleryLightbox";

interface HeroGalleryProps {
  images: string[];
  fallbackImage: string | null;
  hotelName: string;
  subtitle?: string;
}

const HeroGallery: FC<HeroGalleryProps> = ({ images, fallbackImage, hotelName, subtitle }) => {
  const { t } = useTranslation();
  const allImages = images.length > 0 ? images : fallbackImage ? [fallbackImage] : [];
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const small1 = allImages[1] ?? allImages[0] ?? null;
  const small2 = allImages[2] ?? allImages[0] ?? null;
  const extraCount = allImages.length - 3;

  if (!allImages.length) {
    return (
      <div className="flex h-[300px] w-full items-center justify-center rounded-lg bg-background-subtle text-sm text-font-minimal md:h-[450px]">
        {t("Hotels.common.noImagesAvailable")}
      </div>
    );
  }

  const openAt = (index: number) => setLightboxIndex(Math.min(index, allImages.length - 1));

  return (
    <>
      <div className="h-75 md:h-114 grid grid-cols-12 grid-rows-1 gap-3 rounded-lg">
        {/* Main image */}
        <button
          type="button"
          onClick={() => openAt(0)}
          className="group relative col-span-12 overflow-hidden rounded-lg bg-background-subtle md:col-span-8"
        >
          <img
            src={allImages[0]}
            alt={hotelName}
            className="h-full w-full object-cover transition group-hover:scale-[1.02]"
          />
        </button>

        {/* Two stacked small images — only show when we have at least 2 images */}
        {allImages.length >= 2 && (
          <div className="hidden flex-col gap-3 md:col-span-4 md:flex">
            <button
              type="button"
              onClick={() => openAt(1)}
              className="group flex-1 overflow-hidden rounded-lg bg-background-subtle"
            >
              {small1 && (
                <img
                  src={small1}
                  alt={`${hotelName} view 2`}
                  className="h-full w-full object-cover transition group-hover:scale-[1.02]"
                />
              )}
            </button>
            <button
              type="button"
              onClick={() => openAt(2)}
              className="group relative flex-1 overflow-hidden rounded-lg bg-background-subtle"
            >
              {small2 && (
                <img
                  src={small2}
                  alt={`${hotelName} view 3`}
                  className="h-full w-full object-cover transition group-hover:scale-[1.02]"
                />
              )}
              {extraCount > 0 && (
                <div className="absolute inset-0 flex items-center justify-center bg-background-overlay/40 text-2xl font-bold text-font-inverted">
                  +{extraCount}
                </div>
              )}
            </button>
          </div>
        )}
      </div>

      {lightboxIndex !== null && (
        <HeroGalleryLightbox
          images={allImages}
          initialIndex={lightboxIndex}
          title={hotelName}
          subtitle={subtitle}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
};

export default HeroGallery;
